import { Typography } from "@hellocarbo/lasagne";
import Card from "@ui/Card";
import { PieChartRef } from "@ui/PieChart";
import { Diff, Upload } from "lucide-react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { CategoryStyles } from "@/config";
import { Review, ReviewCategory } from "@/types";
import { formatCarbon, getCarbonDisplayUnit } from "@/utils/carbon";

import ReviewCard from "./ReviewCard";
import ReviewPie from "./ReviewPie";

function CarbonEmissionPanel({ data }: { data: Review }) {
  const { reviewId, admin, slug } = useParams();
  const { t } = useTranslation([
    "common",
    "formatter",
    "review",
    "reviewCategories",
  ]);

  const chartRef = useRef<PieChartRef>(null);

  const {
    review_categories: reviewCategoriesData,
    carbon,
    show_comparison: showComparison,
  } = data;

  const handleChartClick = (key: string | undefined) => {
    const reviewCategoryId = reviewCategoriesData.find(
      ({ key: reviewCategoryKey }) => reviewCategoryKey === key
    )?.id;
    if (!reviewCategoryId) {
      return;
    }

    const linkUrl = reviewId
      ? `/reviews/${reviewId}/review_categories/${reviewCategoryId}${admin ? "/admin" : ""}`
      : `/reviews/${slug}/public/${reviewCategoryId}`;

    window.location.assign(linkUrl);
  };

  const { formattedCarbon: formattedCarbonTotal, unit: totalUnit } =
    formatCarbon(t, carbon);

  const reviewCategoriesUnit = getCarbonDisplayUnit(
    reviewCategoriesData.map((reviewCategory) => reviewCategory.carbon)
  );

  return (
    <Card>
      <Typography step="2" weight="bold" as="h2" className="mb-sm">
        {t("review:carbonEmissions.title")}
      </Typography>
      <div className="gap-lg 2xl:gap-xl grid 2xl:grid-cols-[1fr_3fr]">
        <div className="min-h-[225px]">
          <ReviewPie
            data={reviewCategoriesData}
            title={formattedCarbonTotal}
            titleSubText={t(`common:unit.${totalUnit}`)}
            chartRef={chartRef}
            onClick={handleChartClick}
          />
        </div>
        <ul className="gap-sm grid xl:grid-cols-2">
          {reviewCategoriesData
            .filter((category: ReviewCategory) => !!category.carbon)
            .map((category: ReviewCategory) => {
              const { formattedCarbon } = formatCarbon(
                t,
                category.carbon,
                reviewCategoriesUnit
              );

              const { Icon } = CategoryStyles[category.key];

              return (
                <li key={category.key}>
                  <ReviewCard
                    url={
                      reviewId
                        ? `/reviews/${reviewId}/review_categories/${category.id}${
                            admin ? "/admin" : ""
                          }`
                        : `/reviews/${slug}/public/${category.id}`
                    }
                    Icon={Icon}
                    title={t(`reviewCategories:${category.key}`)}
                    subtitle={`${formattedCarbon} ${t(`common:unit.${reviewCategoriesUnit}`)}`}
                  />
                </li>
              );
            })}
          {showComparison && (
            <li key="sectoralComparison">
              <ReviewCard
                title={t("review:comparison.sectoralComparison")}
                url={`/reviews/${reviewId}/comparison`}
                Icon={Diff}
                buttonIntent="secondary"
              />
            </li>
          )}
          <li key="exports">
            <ReviewCard
              title={t("review:exports.title")}
              url={`/reviews/${reviewId}/exports`}
              Icon={Upload}
              buttonIntent="secondary"
            />
          </li>
        </ul>
      </div>
    </Card>
  );
}

export default CarbonEmissionPanel;
